import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar/Navbar';
import Sidebar from '../../components/Sidebar/Sidebar';
import { User } from '../../types/index';
import { generateUsers } from '../../utils/mockData';
import styles from './Users.module.scss';

const API_URL = 'https://69a774032cd1d0552690af5c.mockapi.io/api/users?limit=100';

const Users: React.FC = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [filterOrg, setFilterOrg] = useState('');
  const [filterUsername, setFilterUsername] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [filterPhone, setFilterPhone] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    const auth = localStorage.getItem('lendsqr_auth');
    if (!auth) navigate('/login');
  }, [navigate]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await fetch(API_URL);
        const data = await response.json();

        const apiUsers: User[] = Array.isArray(data) ? data : data.users || [];

        // Normalize API users — fill missing fields with defaults
        const normalizedApiUsers: User[] = apiUsers.map((u, i) => ({
          id: u.id || `USR-${String(i + 1).padStart(4, '0')}`,
          firstName: u.firstName || 'Unknown',
          lastName: u.lastName || 'User',
          email: u.email || `user${i}@example.com`,
          phone: u.phone || '08000000000',
          username: u.username || `user_${i}`,
          organization: u.organization || 'Lendsqr',
          dateJoined: u.dateJoined || 'Jan 1, 2020 10:00 AM',
          status: u.status || 'inactive',
          accountBalance: u.accountBalance || 0,
          bvn: u.bvn || '00000000000',
          accountNumber: u.accountNumber || '0000000000',
          bank: u.bank || 'Access Bank',
          gender: u.gender || 'Male',
          maritalStatus: u.maritalStatus || 'Single',
          children: u.children || 0,
          residenceType: u.residenceType || "Rented Apartment",
          educationLevel: u.educationLevel || 'B.Sc',
          employmentStatus: u.employmentStatus || 'Employed',
          employmentSector: u.employmentSector || 'FinTech',
          employmentDuration: u.employmentDuration || '1 year',
          officeEmail: u.officeEmail || `user${i}@work.com`,
          monthlyIncome: u.monthlyIncome || 50000,
          loanRepayment: u.loanRepayment || 0,
          twitter: u.twitter || `@user${i}`,
          facebook: u.facebook || `fb.com/user${i}`,
          instagram: u.instagram || `@user${i}`,
          tier: u.tier || 1,
          guarantor1FirstName: u.guarantor1FirstName || 'John',
          guarantor1LastName: u.guarantor1LastName || 'Doe',
          guarantor1Phone: u.guarantor1Phone || '08000000001',
          guarantor1Email: u.guarantor1Email || 'guarantor@gmail.com',
          guarantor1Relationship: u.guarantor1Relationship || 'Friend',
          guarantor2FirstName: u.guarantor2FirstName || 'Jane',
          guarantor2LastName: u.guarantor2LastName || 'Doe',
          guarantor2Phone: u.guarantor2Phone || '08000000002',
          guarantor2Email: u.guarantor2Email || 'guarantor2@gmail.com',
          guarantor2Relationship: u.guarantor2Relationship || 'Sibling',
        }));

        console.log('API returned:', normalizedApiUsers.length, 'users');

        const remaining = 500 - normalizedApiUsers.length;
        const generatedUsers = generateUsers(remaining, normalizedApiUsers.length);

        console.log('Generated:', generatedUsers.length, 'users');

        const combined = [...normalizedApiUsers, ...generatedUsers];

        console.log('Total:', combined.length, 'users');

        setAllUsers(combined);
        setFilteredUsers(combined);
      } catch (error) {
        console.log('API failed, using generated data');
        const generatedUsers = generateUsers(500, 0);
        setAllUsers(generatedUsers);
        setFilteredUsers(generatedUsers);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setActiveDropdown(null);
        setActiveFilter(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const totalPages = Math.ceil(filteredUsers.length / perPage);
  const startIndex = (currentPage - 1) * perPage;
  const currentUsers = filteredUsers.slice(startIndex, startIndex + perPage);

  const handleFilter = () => {
    let result = [...allUsers];
    if (filterOrg) result = result.filter(u => u.organization.toLowerCase().includes(filterOrg.toLowerCase()));
    if (filterUsername) result = result.filter(u => u.username.toLowerCase().includes(filterUsername.toLowerCase()));
    if (filterEmail) result = result.filter(u => u.email.toLowerCase().includes(filterEmail.toLowerCase()));
    if (filterPhone) result = result.filter(u => u.phone.includes(filterPhone));
    if (filterStatus) result = result.filter(u => u.status === filterStatus);
    setFilteredUsers(result);
    setCurrentPage(1);
    setActiveFilter(null);
  };

  const handleReset = () => {
    setFilterOrg('');
    setFilterUsername('');
    setFilterEmail('');
    setFilterDate('');
    setFilterPhone('');
    setFilterStatus('');
    setFilteredUsers(allUsers);
    setCurrentPage(1);
    setActiveFilter(null);
  };

  const handleViewDetails = (user: User) => {
    localStorage.setItem('lendsqr_user', JSON.stringify(user));
    navigate(`/users/${user.id}`);
  };

  const handleBlacklist = (userId: string) => {
    const updated = allUsers.map(u =>
      u.id === userId ? { ...u, status: 'blacklisted' as User['status'] } : u
    );
    setAllUsers(updated);
    setFilteredUsers(updated);
    setActiveDropdown(null);
  };

  const handleActivate = (userId: string) => {
    const updated = allUsers.map(u =>
      u.id === userId ? { ...u, status: 'active' as User['status'] } : u
    );
    setAllUsers(updated);
    setFilteredUsers(updated);
    setActiveDropdown(null);
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const stats = [
    {
      label: 'Users',
      value: allUsers.length.toLocaleString(),
      colorClass: 'pink',
      icon: <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="#DF18FF" strokeWidth={1.8}><path strokeLinecap="round" d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path strokeLinecap="round" d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>,
    },
    {
      label: 'Active Users',
      value: allUsers.filter(u => u.status === 'active').length.toLocaleString(),
      colorClass: 'purple',
      icon: <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="#5718B2" strokeWidth={1.8}><path strokeLinecap="round" d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path strokeLinecap="round" d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>,
    },
    {
      label: 'Users with Loans',
      value: allUsers.filter(u => u.loanRepayment > 0).length.toLocaleString(),
      colorClass: 'red',
      icon: <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="#E4033B" strokeWidth={1.8}><path strokeLinecap="round" d="M9 8h6m-5 0a3 3 0 110 6H9l3 3m-3-6h6m6 1a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>,
    },
    {
      label: 'Users with Savings',
      value: allUsers.filter(u => u.accountBalance > 0).length.toLocaleString(),
      colorClass: 'gold',
      icon: <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="#E9B200" strokeWidth={1.8}><path strokeLinecap="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2z"/></svg>,
    },
  ];

  return (
    <div className={styles.usersLayout}>
      <Sidebar isOpen={sidebarOpen} />
      <div className={styles.mainContent}>
        <Navbar onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
        <div className={styles.pageContent}>
          <div className={styles.pageHeader}>
            <h1>Users</h1>
          </div>

          <div className={styles.statsGrid}>
            {stats.map((stat, index) => (
              <div key={index} className={styles.statCard}>
                <div className={`${styles.statIcon} ${styles[stat.colorClass]}`}>
                  {stat.icon}
                </div>
                <p className={styles.statLabel}>{stat.label}</p>
                <h3 className={styles.statValue}>{stat.value}</h3>
              </div>
            ))}
          </div>

          <div className={styles.tableCard}>
            {loading ? (
              <div className={styles.loadingWrap}>
                <div className={styles.spinner} />
                <p className={styles.loadingText}>Loading users...</p>
              </div>
            ) : (
              <>
                <div className={styles.tableWrap}>
                  <table>
                    <thead>
                      <tr>
                        {['Organization','Username','Email','Phone Number','Date Joined','Status'].map((col) => (
                          <th key={col}>
                            <div
                              className={styles.thInner}
                              onClick={() => setActiveFilter(activeFilter === col ? null : col)}
                            >
                              {col}
                              <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                              </svg>
                            </div>
                            {activeFilter === col && (
                              <div className={styles.filterPopup}>
                                <div className={styles.filterGroup}>
                                  <label>Organization</label>
                                  <select value={filterOrg} onChange={e => setFilterOrg(e.target.value)}>
                                    <option value="">Select</option>
                                    {['Lendsqr','Irorun','Lendstar','Kuda','Cowrywise','PiggyVest','Carbon','FairMoney','Renmoney','Palmpay'].map(o => (
                                      <option key={o} value={o}>{o}</option>
                                    ))}
                                  </select>
                                </div>
                                <div className={styles.filterGroup}>
                                  <label>Username</label>
                                  <input type="text" placeholder="User" value={filterUsername} onChange={e => setFilterUsername(e.target.value)}/>
                                </div>
                                <div className={styles.filterGroup}>
                                  <label>Email</label>
                                  <input type="text" placeholder="Email" value={filterEmail} onChange={e => setFilterEmail(e.target.value)}/>
                                </div>
                                <div className={styles.filterGroup}>
                                  <label>Date</label>
                                  <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)}/>
                                </div>
                                <div className={styles.filterGroup}>
                                  <label>Phone Number</label>
                                  <input type="text" placeholder="Phone Number" value={filterPhone} onChange={e => setFilterPhone(e.target.value)}/>
                                </div>
                                <div className={styles.filterGroup}>
                                  <label>Status</label>
                                  <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                                    <option value="">Select</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="pending">Pending</option>
                                    <option value="blacklisted">Blacklisted</option>
                                  </select>
                                </div>
                                <div className={styles.filterActions}>
                                  <button className={styles.btnOutline} onClick={handleReset}>Reset</button>
                                  <button className={styles.btnPrimary} onClick={handleFilter}>Filter</button>
                                </div>
                              </div>
                            )}
                          </th>
                        ))}
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentUsers.length === 0 ? (
                        <tr>
                          <td colSpan={7}>
                            <div className={styles.emptyState}>
                              <svg width="60" height="60" fill="none" viewBox="0 0 24 24" stroke="#8B9AB7" strokeWidth={1}>
                                <path strokeLinecap="round" d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                                <circle cx="9" cy="7" r="4"/>
                              </svg>
                              <p>No users found</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        currentUsers.map((user) => (
                          <tr key={user.id}>
                            <td>{user.organization}</td>
                            <td>{user.username}</td>
                            <td>{user.email}</td>
                            <td>{user.phone}</td>
                            <td>{user.dateJoined}</td>
                            <td>
                              <span className={`${styles.statusBadge} ${styles[user.status || 'inactive']}`}>
                                {user.status
                                  ? user.status.charAt(0).toUpperCase() + user.status.slice(1)
                                  : 'Inactive'}
                              </span>
                            </td>
                            <td>
                              <div className={styles.actionWrap}>
                                <button
                                  className={styles.actionBtn}
                                  onClick={() => setActiveDropdown(activeDropdown === user.id ? null : user.id)}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#545F7D">
                                    <circle cx="12" cy="5" r="1.5"/>
                                    <circle cx="12" cy="12" r="1.5"/>
                                    <circle cx="12" cy="19" r="1.5"/>
                                  </svg>
                                </button>
                                {activeDropdown === user.id && (
                                  <div className={styles.dropdownMenu}>
                                    <button className={styles.dropdownItem} onClick={() => handleViewDetails(user)}>
                                      <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                                        <path strokeLinecap="round" d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                        <circle cx="12" cy="12" r="3"/>
                                      </svg>
                                      View Details
                                    </button>
                                    <button className={styles.dropdownItem} onClick={() => handleBlacklist(user.id)}>
                                      <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                                        <path strokeLinecap="round" d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                                        <circle cx="8.5" cy="7" r="4"/>
                                        <line x1="18" y1="8" x2="23" y2="13"/>
                                        <line x1="23" y1="8" x2="18" y2="13"/>
                                      </svg>
                                      Blacklist User
                                    </button>
                                    <button className={styles.dropdownItem} onClick={() => handleActivate(user.id)}>
                                      <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                                        <path strokeLinecap="round" d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                                        <circle cx="8.5" cy="7" r="4"/>
                                        <polyline points="17 11 19 13 23 9"/>
                                      </svg>
                                      Activate User
                                    </button>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className={styles.paginationWrap}>
                  <div className={styles.pageInfo}>
                    Showing
                    <select
                      className={styles.perPageSelect}
                      value={perPage}
                      onChange={e => {
                        setPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    out of {filteredUsers.length}
                  </div>

                  <div className={styles.pageBtns}>
                    <button
                      className={styles.pageBtn}
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <polyline points="15 18 9 12 15 6"/>
                      </svg>
                    </button>

                    {getPageNumbers().map((page, index) =>
                      page === '...' ? (
                        <span key={index} className={styles.pageDots}>...</span>
                      ) : (
                        <button
                          key={index}
                          className={`${styles.pageBtn} ${currentPage === page ? styles.active : ''}`}
                          onClick={() => setCurrentPage(page as number)}
                        >
                          {page}
                        </button>
                      )
                    )}

                    <button
                      className={styles.pageBtn}
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Users;